import React, { useState } from "react";
import { motion, useInView, AnimatePresence, useScroll, useSpring } from "framer-motion";
import heroMain from "../assets/hero_main.png";
import timeTrackImg from "../assets/track_time.png";
import reportsImg from "../assets/Reports.jpg";
import rotaImg from "../assets/Rota.jpg";
import multiFactorImg from "../assets/muti_factor.jpg";
import staffAppImg from "../assets/staffapp.jpg";
import {
  FaClock,
  FaUserClock,
  FaChartLine,
  FaMobileAlt,
  FaShieldAlt,
  FaBell,
  FaFileExport,
  FaUsers,
  FaCheckCircle,
  FaArrowRight,
  FaCalendarAlt,
  FaUtensils,
  FaStore,
  FaHotel,
  FaHospital,
  FaGlassCheers,
  FaHeadset,
  FaGraduationCap,
  FaHandHoldingHeart,
  FaAmbulance,
  FaUserNurse,
  FaChevronDown,
  FaChevronUp,
  FaPlay,
} from "react-icons/fa";
import ContactForm from "../components/ContactForm";
import NewsletterSignup from "../components/NewsletterSignup";

/* ─── Data ───────────────────────────────────────── */

const features = [
  {
    title: "Track Time with Clock In, Clock Out & Breaks",
    desc: "Effortlessly monitor employee working hours with a simple clock-in and clock-out system.",
    points: [
      "Real-time tracking – Know exactly when your employees start and finish work.",
      "Break management – Track employee break times to maintain compliance.",
      "Unique PINs for security – Each staff member gets a secure PIN for clocking in/out.",
    ],
    image: timeTrackImg,
    icon: <FaClock />,
    accent: "from-blue-500 to-cyan-400",
  },
  {
    title: "Reports – Aggregated & Daily Insights",
    desc: "Generate detailed reports to track employee hours, attendance, and wages.",
    points: [
      "Aggregated reports – View attendance data for any custom range.",
      "Daily reports – See individual work logs for each employee.",
      "Export & email reports – Generate and send them directly to your inbox.",
    ],
    image: reportsImg,
    icon: <FaChartLine />,
    accent: "from-violet-500 to-purple-400",
  },
  {
    title: "Rota for Staff – Plan & Track Availability",
    desc: "Assign employees to work schedules and track attendance effortlessly.",
    points: [
      "Customizable rota system – Assign shifts based on availability.",
      "Live attendance tracking – See who's working, absent, or on leave.",
      "Boost efficiency – Ensure optimal staffing levels at all times.",
    ],
    image: rotaImg,
    icon: <FaCalendarAlt />,
    accent: "from-emerald-500 to-teal-400",
  },
  {
    title: "Multi-Factor Clock-In for Enhanced Security",
    desc: "Ensure secure clock-ins with multi-factor authentication.",
    points: [
      "PIN-based clock-in – Each staff member gets a unique PIN.",
      "Face ID authentication – AI-powered clock-ins and clock-outs.",
      "Prevent buddy punching – Ensure the right person is clocking in.",
    ],
    image: multiFactorImg,
    icon: <FaShieldAlt />,
    accent: "from-amber-500 to-orange-400",
  },
  {
    title: "Employee App – Stay Organized & Track Hours",
    desc: "Empower your staff with an easy-to-use scheduling app.",
    points: [
      "Daily Work Logs – Track daily login hours and attendance history.",
      "Past Attendance Reports – Access previous work logs for payroll accuracy.",
      "Labour Cost Management – Optimize workforce expenses effortlessly.",
    ],
    image: staffAppImg,
    icon: <FaMobileAlt />,
    accent: "from-rose-500 to-pink-400",
  },
];

const industries = [
  { icon: <FaUtensils />, title: "Food & Beverage", desc: "Restaurants, cafes, and food service" },
  { icon: <FaStore />, title: "Retail", desc: "Shops and retail operations" },
  { icon: <FaHotel />, title: "Hospitality", desc: "Hotels and accommodation" },
  { icon: <FaHospital />, title: "Healthcare", desc: "Medical staff and clinics" },
  { icon: <FaGlassCheers />, title: "Events", desc: "Events and entertainment" },
  { icon: <FaHeadset />, title: "Call Centers", desc: "Customer service teams" },
  { icon: <FaGraduationCap />, title: "Education", desc: "Schools and universities" },
  { icon: <FaHandHoldingHeart />, title: "Nonprofits", desc: "Volunteer coordination" },
  { icon: <FaAmbulance />, title: "Emergency Services", desc: "First responder teams" },
  { icon: <FaUserNurse />, title: "Caregiving", desc: "Home care and nursing" },
];

const videos = [
  {
    src: "https://www.youtube.com/embed/1L2VahazEk4",
    title: "Effortless Time Tracking & Payroll Automation",
    desc: "See how ClockIn Master simplifies time tracking and automates payroll in real time.",
  },
  {
    src: "https://www.youtube.com/embed/rKnwyc5lCT0",
    title: "How to Clock In, Take Breaks, and Clock Out",
    desc: "Step-by-step guide for accurate attendance records and hassle-free reporting.",
  },
  {
    src: "https://www.youtube.com/embed/oz5AUfkZcK8",
    title: "Understanding Staff Attendance Reports",
    desc: "Learn to access and interpret attendance reports for better workforce management.",
  },
];

const stats = [
  { value: "99.9%", label: "Uptime" },
  { value: "10K+", label: "Clock-ins Daily" },
  { value: "500+", label: "Businesses" },
  { value: "24/7", label: "Support" },
];

/* ─── Sub-components ─────────────────────────────── */

const FeatureSection = ({ feature, index }) => {
  const isEven = index % 2 === 0;
  const sectionRef = React.useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={sectionRef}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: 0.1, type: "spring", stiffness: 80 }}
      className="py-16"
    >
      <div className={`flex flex-col ${isEven ? "lg:flex-row" : "lg:flex-row-reverse"} items-center gap-12 lg:gap-20`}>
        {/* Image */}
        <motion.div
          whileHover={{ scale: 1.02 }}
          transition={{ type: "spring", stiffness: 300 }}
          className="w-full lg:w-1/2 relative"
        >
          <div className={`absolute -inset-4 bg-gradient-to-br ${feature.accent} rounded-3xl opacity-10 blur-2xl`} />
          <div className="relative rounded-2xl overflow-hidden shadow-card-hover border border-gray-100">
            <img
              src={feature.image}
              alt={feature.title}
              className="w-full h-auto object-cover min-h-[240px] max-h-[380px]"
            />
          </div>
        </motion.div>

        {/* Content */}
        <div className="w-full lg:w-1/2">
          <div className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br ${feature.accent} text-white text-xl mb-6 shadow-lg`}>
            {feature.icon}
          </div>
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 leading-tight">
            {feature.title}
          </h3>
          <p className="text-muted text-lg mb-6 leading-relaxed">{feature.desc}</p>
          <div className="space-y-3">
            {feature.points.map((point, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -16 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.3 + i * 0.1 }}
                className="flex items-start gap-3"
              >
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center mt-0.5">
                  <FaCheckCircle className="text-accent text-xs" />
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{point}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const IndustryCard = ({ industry, index }) => {
  const cardRef = React.useRef(null);
  const isInView = useInView(cardRef, { once: true, margin: "-60px" });

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 24 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.06, type: "spring" }}
      whileHover={{ y: -4, boxShadow: "0 12px 40px rgba(0,0,0,0.1)" }}
      className="bg-white rounded-2xl shadow-card p-6 flex flex-col items-center text-center border border-gray-100 transition-all duration-300 cursor-default"
    >
      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-secondary/10 to-accent/10 flex items-center justify-center mb-4">
        {React.cloneElement(industry.icon, { className: "text-2xl text-secondary" })}
      </div>
      <h3 className="text-base font-bold text-gray-900 mb-1">{industry.title}</h3>
      <p className="text-muted text-xs leading-relaxed">{industry.desc}</p>
    </motion.div>
  );
};

/* ─── Main Component ─────────────────────────────── */

const Home = () => {
  const [expandedFeature, setExpandedFeature] = useState(null);
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });

  const toggleFeature = (index) => setExpandedFeature(expandedFeature === index ? null : index);

  return (
    <main>
      {/* Scroll progress */}
      <motion.div className="fixed top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-secondary to-accent origin-left z-[60]" style={{ scaleX }} />

      {/* ─── HERO ──────────────────────────────────── */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        id="hero-section"
        className="relative bg-hero-gradient text-white pt-28 md:pt-36 pb-20 md:pb-28 overflow-hidden min-h-[70vh] flex items-center"
      >
        {/* Mesh background */}
        <div className="absolute inset-0 bg-hero-mesh" />
        {/* Animated orbs */}
        <div className="absolute top-[10%] left-[10%] w-72 h-72 bg-secondary/10 rounded-full blur-3xl animate-pulse-glow" />
        <div className="absolute bottom-[10%] right-[10%] w-96 h-96 bg-accent/5 rounded-full blur-3xl animate-pulse-glow" style={{ animationDelay: "1.5s" }} />

        {/* Floating icon bubbles */}
        {[
          { Icon: FaClock, pos: "top-[15%] left-[8%]", delay: 0 },
          { Icon: FaChartLine, pos: "top-[25%] right-[20%]", delay: 1 },
          { Icon: FaShieldAlt, pos: "bottom-[30%] left-[15%]", delay: 2 },
          { Icon: FaUsers, pos: "top-[60%] right-[8%]", delay: 0.5 },
        ].map(({ Icon, pos, delay }, i) => (
          <motion.div
            key={i}
            animate={{ y: [0, -16, 0] }}
            transition={{ duration: 6 + i, repeat: Infinity, ease: "easeInOut", delay }}
            className={`absolute ${pos} w-10 h-10 bg-white/10 rounded-xl backdrop-blur-sm flex items-center justify-center hidden md:flex`}
          >
            <Icon className="text-white/60 text-sm" />
          </motion.div>
        ))}

        <div className="container mx-auto px-6 relative z-10 flex flex-col-reverse lg:flex-row items-center justify-between gap-12">
          {/* Left: Text */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="w-full lg:w-1/2"
          >
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm text-sm text-white/80 font-medium mb-6 border border-white/10"
            >
              <span className="w-2 h-2 bg-accent rounded-full animate-pulse" />
              Workforce Management Simplified
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 leading-[1.1] tracking-tight"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Efficient Attendance Tracking &{" "}
              <span className="bg-gradient-to-r from-blue-300 to-accent bg-clip-text text-transparent">
                Payroll Simplified
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: -16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="text-lg md:text-xl mb-8 max-w-xl text-white/70 leading-relaxed"
            >
              Schedule employees, track time, manage payroll, and communicate with your team — all from one powerful platform.
            </motion.p>

            {/* App store badges */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="flex flex-wrap gap-3 mb-8"
            >
              <a
                href="https://play.google.com/store/apps/details?id=com.foodhub.shiftbuddy&hl=en_GB"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-transform hover:scale-105"
              >
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                  alt="Get it on Google Play"
                  style={{ height: "48px" }}
                />
              </a>
              <a
                href="https://apps.apple.com/pl/app/clock-in-master/id6680192687"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-transform hover:scale-105"
              >
                <img
                  src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                  alt="Download on the App Store"
                  style={{ height: "48px" }}
                />
              </a>
            </motion.div>

            {/* Stats row */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="flex flex-wrap gap-6 md:gap-10"
            >
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-2xl md:text-3xl font-extrabold text-white">{stat.value}</div>
                  <div className="text-xs text-white/50 font-medium uppercase tracking-wider mt-0.5">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right: Hero image */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="hidden lg:flex w-full lg:w-1/2 justify-center"
          >
            <div className="relative">
              <div className="absolute -inset-6 bg-gradient-to-br from-secondary/20 to-accent/10 rounded-3xl blur-3xl" />
              <img
                src={heroMain}
                alt="ClockInMaster dashboard preview"
                className="relative max-w-2xl w-full rounded-2xl"
                style={{ transform: "scale(1.1)" }}
              />
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* ─── FEATURES ──────────────────────────────── */}
      <section id="features" className="section-padding bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="badge badge-primary mb-4">Features</span>
            <h2 className="section-title text-gray-900 mb-4">
              Powerful Features for Modern{" "}
              <span className="gradient-text">Workforce Management</span>
            </h2>
            <p className="section-subtitle">
              Everything you need to manage your workforce efficiently, from scheduling to payroll.
            </p>
          </motion.div>

          {/* Mobile view */}
          <div className="lg:hidden space-y-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="feature-card overflow-hidden"
              >
                <div className="p-2">
                  <div className="w-full h-48 mb-5 rounded-xl overflow-hidden relative">
                    <img src={feature.image} alt={feature.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent" />
                  </div>
                  <div className={`inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br ${feature.accent} text-white text-lg mb-3`}>
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-muted text-sm mb-3">{feature.desc}</p>

                  <button
                    onClick={() => toggleFeature(index)}
                    className="flex items-center gap-2 text-secondary font-semibold text-sm mb-2"
                  >
                    <span>View Details</span>
                    {expandedFeature === index ? <FaChevronUp className="text-xs" /> : <FaChevronDown className="text-xs" />}
                  </button>
                  <AnimatePresence>
                    {expandedFeature === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-2 pt-2">
                          {feature.points.map((point, i) => (
                            <div key={i} className="flex items-start gap-2 text-sm text-gray-600">
                              <FaCheckCircle className="text-accent text-xs mt-1 flex-shrink-0" />
                              <span>{point}</span>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Desktop view */}
          <div className="hidden lg:block">
            {features.map((feature, idx) => (
              <FeatureSection key={feature.title} feature={feature} index={idx} />
            ))}
          </div>
        </div>
      </section>

      {/* ─── VIDEO TUTORIALS ───────────────────────── */}
      <section id="video-tutorials" className="section-padding bg-lightgray">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <span className="badge badge-primary mb-4">Tutorials</span>
            <h2 className="section-title text-gray-900 mb-4">How to Use ClockInMaster</h2>
            <p className="section-subtitle">
              Watch our quick video guides to get the most out of ClockInMaster.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video, i) => (
              <motion.div
                key={video.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-white rounded-2xl shadow-card overflow-hidden border border-gray-100 hover:shadow-card-hover transition-all duration-300"
              >
                <div className="aspect-video">
                  <iframe
                    width="100%"
                    height="100%"
                    src={video.src}
                    title={video.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
                <div className="p-5">
                  <h3 className="text-base font-bold text-gray-900 mb-2">{video.title}</h3>
                  <p className="text-muted text-sm leading-relaxed">{video.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-10">
            <a
              href="https://www.youtube.com/@ClockInMaster"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary gap-2"
            >
              <FaPlay className="text-xs" />
              See More on YouTube
            </a>
          </div>
        </div>
      </section>

      {/* ─── INDUSTRIES ────────────────────────────── */}
      <section id="industries" className="section-padding bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <span className="badge badge-accent mb-4">Industries</span>
            <h2 className="section-title text-gray-900 mb-4">
              Built for <span className="gradient-text">Your Industry</span>
            </h2>
            <p className="section-subtitle">
              From local restaurants to regional hotels, give your team the power of ClockIn Master.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {industries.map((industry, idx) => (
              <IndustryCard key={industry.title} industry={industry} index={idx} />
            ))}
          </div>
        </div>
      </section>

      {/* ─── CONTACT ───────────────────────────────── */}
      <section id="contact" className="section-padding bg-lightgray">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="section-title text-gray-900 mb-3">Get in Touch</h2>
            <p className="section-subtitle">
              Have questions about ClockIn Master? We're here to help!
            </p>
          </motion.div>
          <ContactForm />
        </div>
      </section>

      {/* ─── NEWSLETTER ────────────────────────────── */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-6 max-w-5xl">
          <NewsletterSignup />
        </div>
      </section>
    </main>
  );
};

export default Home;